/**
 * ProShip Direct — activating a plan.
 *
 * Two payment paths, because the merchant's existing billing decides it:
 *
 *   Postpaid — there is a credit line, so activation is a confirmation and the charge lands on
 *   the invoice. Nothing to collect now.
 *
 *   Prepaid  — it is paid from wallet balance today, so the balance is checked first. If it is
 *   short we say by how much and send them to recharge rather than failing at submit. Activation
 *   stays disabled until the balance covers it.
 *
 * Auto-renew is an explicit consent, not a pre-ticked default buried in terms: it is a recurring
 * charge against the same wallet or credit line, and a merchant should tick it deliberately.
 *
 * TWO STEPS, and the second one is the point of the first being reviewable: REVIEW what will be
 * charged, then VERIFY with a code sent to the registered email. Activation opens a recurring
 * charge and every panel user with SETTINGS access can reach this button, so a second factor is
 * what separates "the account holder decided" from "someone had the tab open".
 *
 * The code comes from ProShip's OTP framework (POST direct/otp); the purchase carries it with its
 * tokenId and the server checks it before charging.
 */
import React, { useState } from 'react';
import {
  Box, Typography, Button, Alert, Divider, FormControlLabel, Checkbox, Chip, CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

import MUIModal from '../../../common/MUIModal';
import ActivationOtpStep from './ActivationOtpStep';
import { primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, SUCCESS, WARNING, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { statLabelSx } from './stats';
import { planGst, planTotal } from '../data/plans';
import { OTP_LENGTH } from '../data/activationOtp';
import useActivationOtp from '../data/useActivationOtp';

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.5 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function PlanActivationModal({ plan, billing, onActivate, onClose }) {
  const [autoRenew, setAutoRenew] = useState(true);
  const [understood, setUnderstood] = useState(false);

  /* REVIEW -> VERIFY. Nothing activates from the review step; it can only send a code. */
  const [step, setStep] = useState('REVIEW');
  const otp = useActivationOtp();
  /*
    Activation now CHARGES. It can be declined — an empty wallet, an exhausted credit line, or
    ProShip Direct not being enabled for this merchant — so the modal has to stay open and show
    the backend's own sentence. Closing optimistically, as this did while it only wrote
    localStorage, would have swallowed a failed payment.
  */
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  const sendCode = async () => {
    setApiError(null);
    if (await otp.send()) setStep('VERIFY');
  };

  /*
    The one path to activation. The code travels with the purchase and the server checks it before
    it charges, so a wrong code buys nothing; its message shows under the code box.
  */
  const verifyAndActivate = async () => {
    const proof = otp.proof();
    if (!proof) return;
    setSubmitting(true);
    setApiError(null);
    try {
      await onActivate({ planId: plan.id, autoRenew, proof });   // the server keeps a trial off auto-renew
      onClose();
    } catch (e) {
      otp.setError(e?.message || 'Activation failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!plan) return null;

  const total = planTotal(plan);
  const isFree = plan.price === 0;
  // The trial is once per account and never renews: no auto-renew to offer, just when it ends.
  const isTrial = plan.id === 'TRIAL';
  const payFromWallet = !billing.isPostpaid;
  const shortfall = payFromWallet ? Math.max(total - billing.walletBalance, 0) : 0;
  const blocked = payFromWallet && !isFree && shortfall > 0;

  const rows = [
    { k: `${plan.name} plan`, v: isFree ? 'Free' : inr(plan.price) },
    { k: 'GST (18%)', v: isFree ? '—' : inr(planGst(plan)) },
    { k: 'Shipments included', v: plan.shipments.toLocaleString('en-IN') },
    { k: 'Beyond that, per shipment', v: `₹${plan.overagePrice.toFixed(2)}` },
    { k: 'Valid for', v: `${plan.validityDays} days` },
  ];

  return (
    <MUIModal
      open
      handleClose={onClose}
      maxWidth='sm'
      title={
        step === 'VERIFY' ? 'Verify to activate' : `Activate the ${plan.name} plan`
      }
      action={
        <Box sx={{ display: 'flex', gap: GAP.sm }}>
          {/* Back, not Cancel, once a code is out — a mistyped digit should not cost the review. */}
          <Button size='small' sx={secondaryBtnSx} onClick={step === 'VERIFY' ? () => setStep('REVIEW') : onClose}>
            {step === 'VERIFY' ? 'Back' : 'Cancel'}
          </Button>
          {step === 'REVIEW' ? (
            <Button
              size='small'
              variant='contained'
              sx={primaryBtnSx}
              disabled={blocked || !understood || otp.sending}
              onClick={sendCode}
              startIcon={otp.sending ? <CircularProgress size={12} /> : null}
            >
              {otp.sending ? 'Sending code' : 'Send code to continue'}
            </Button>
          ) : (
            <Button
              size='small'
              variant='contained'
              sx={primaryBtnSx}
              disabled={blocked || otp.entered.length !== OTP_LENGTH || submitting}
              onClick={verifyAndActivate}
              startIcon={submitting ? <CircularProgress size={12} /> : null}
            >
              {submitting
                ? 'Charging…'
                : isFree ? 'Verify and start trial' : `Verify and pay ${inr(total)}`}
            </Button>
          )}
        </Box>
      }
    >
      {(apiError || (step === 'REVIEW' && otp.error)) && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {apiError || otp.error}
        </Alert>
      )}
      {step === 'VERIFY' ? (
        <ActivationOtpStep {...otp.stepProps} />
      ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
        <Box sx={{ p: 1.25, bgcolor: LIGHT_BG, borderRadius: RADIUS.md }}>
          {rows.map((row) => (
            <Box key={row.k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
              <Typography sx={smallSx}>{row.k}</Typography>
              <Typography sx={{ ...smallSx, color: NAVY }}>{row.v}</Typography>
            </Box>
          ))}
          <Divider sx={{ my: 0.75, borderColor: BORDER }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={strongSx}>Payable now</Typography>
            <Typography sx={strongSx}>{isFree ? 'Nothing' : inr(total)}</Typography>
          </Box>
        </Box>

        {/* How it gets paid for */}
        <Box>
          <Typography sx={{ ...statLabelSx, mb: 0.5 }}>Paid from</Typography>
          {billing.isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm }}>
              <CircularProgress size={14} />
              <Typography sx={smallSx}>Checking your billing setup…</Typography>
            </Box>
          ) : billing.isPostpaid ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm }}>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 17, color: PRIMARY_BLUE }} />
              <Box>
                <Typography sx={strongSx}>Postpaid credit</Typography>
                <Typography sx={smallSx}>
                  Added to your invoice — nothing is deducted today.
                  {billing.creditAvailable > 0 && ` ${inr(billing.creditAvailable)} credit available.`}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm }}>
              <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 17, color: blocked ? WARNING : SUCCESS }} />
              <Box>
                <Typography sx={strongSx}>Prepaid wallet</Typography>
                <Typography sx={smallSx}>Balance {inr(billing.walletBalance)}</Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <Chip
                size='small'
                label={blocked ? `${inr(shortfall)} short` : 'Sufficient'}
                sx={{
                  fontFamily: FONT,
                  fontSize: SIZE['4xs'],
                  fontWeight: WEIGHT.semibold,
                  bgcolor: blocked ? `${WARNING}14` : `${SUCCESS}14`,
                  color: blocked ? WARNING : SUCCESS,
                }}
              />
            </Box>
          )}
        </Box>

        {blocked && (
          <Alert
            severity='warning'
            sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.25 }}
            action={
              <Button component={Link} to='/wallet' size='small' sx={secondaryBtnSx}>
                Recharge wallet
              </Button>
            }
          >
            Your wallet is {inr(shortfall)} short of {inr(total)}. Recharge and come back — nothing
            is activated until the full amount is available.
          </Alert>
        )}

        {isTrial ? (
          <Typography sx={smallSx}>
            The trial runs for {plan.validityDays} days and does not renew. It can be used once per account.
            Choose a plan any time to continue on your own couriers.
          </Typography>
        ) : (
        <Box>
          <FormControlLabel
            control={<Checkbox size='small' checked={autoRenew} onChange={() => setAutoRenew(!autoRenew)} />}
            label={
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY }}>
                Renew automatically every month
              </Typography>
            }
          />
          <Typography sx={{ ...smallSx, ml: 3.75, mt: -0.5 }}>
            {autoRenew
              ? `${isFree ? 'After the trial, ' : ''}${inr(total)} will be charged to your ${billing.isPostpaid ? 'invoice' : 'wallet'} every ${plan.validityDays} days until you turn this off.`
              : 'The plan will simply expire at the end of the period, and Direct couriers stop being allocated.'}
          </Typography>
        </Box>
        )}

        <Box sx={{ p: 1.25, border: `1px solid ${BORDER}`, borderRadius: RADIUS.md }}>
          <FormControlLabel
            control={<Checkbox size='small' checked={understood} onChange={() => setUnderstood(!understood)} />}
            label={
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY }}>
                I understand what this plan does and does not cover
              </Typography>
            }
          />
          <Typography sx={{ ...smallSx, ml: 3.75, mt: -0.5 }}>
            Shipments beyond {plan.shipments.toLocaleString('en-IN')} are billed at ₹
            {plan.overagePrice.toFixed(2)} each. Buyer communications — WhatsApp, SMS and email —
            are not included and are switched on separately from Settings.
          </Typography>
        </Box>

        <Typography sx={bodySx}>
          Your ProShip couriers are unaffected either way. Direct adds your own contracts alongside
          them.
        </Typography>
      </Box>
      )}
    </MUIModal>
  );
}
